import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import {
  toFrenchNumber,
  formattedColorMaterial,
} from "../../../components/formatted/FormattedText";
import HorizontalBarChart from "../../../components/charts/HorizontalBarChart";
import VerticalBarChart from "../../../components/charts/VerticalBarChart";
import { MaterialCountingPieChart } from "../../../components/charts/MaterialCountingPieChart";
import useCharacterizationMetrics from "../core/hooks/useCharacterizationMetrics";
import { capitalizeFirstLetter } from "../../../components/utils/Date";
import {
  getParentCategoryLabelPlural,
  sortRepAssociatedKeys,
} from "../core/utils/utils";
import { REGULATION_TYPES } from "./constants/regulation";

export const ContainerViewMaterialForPDF = ({
  refs,
  parentCategoriesList = [],
  totalWasteMass = 0,
  totalWasteVolume = 0,
  materialDelivery = [],
  recoverableMaterialList = [],
  totalVolumeRecoverable = 0,
  totalMassRecoverable = 0,
  recoverableWithOutlet = 0,
  recoverableWithNoOutlet = 0,
  materialTotal = 0,
  outletData = {},
  totalWeight = 0,
  repData = {},
  characterizationType = "1199",
  sizeThreshold = 40,
  totalFinesMass = 0,
  totalFinesVolume = 0,
  finesBelowThreshold = {},
  sizeRangeDistribution = null,
  sizeRangeMatrix = null,
  isBenneCharacterization = false,
  // When true, render off-screen (used in detail page). When false (export from list),
  // let the parent container handle positioning so charts can render normally.
  renderOffscreen = true,
}) => {
  const { t } = useTranslation();
  const metrics = useCharacterizationMetrics({
    materialDelivery,
    totalWasteMass,
    totalWasteVolume,
    recoverableMaterialList,
    parentCategoriesList,
    totalVolumeRecoverable,
    totalMassRecoverable,
    recoverableWithOutlet,
    recoverableWithNoOutlet,
  });

  const effectiveThresholdCm = useMemo(() => {
    if (characterizationType === "1199") return 40;
    if (characterizationType === "RHW" || characterizationType === "OMR")
      return 0;
    return typeof sizeThreshold === "number" ? sizeThreshold : 40;
  }, [characterizationType, sizeThreshold]);

  const finesLabel = useMemo(
    () =>
      effectiveThresholdCm > 0
        ? t("Fines + objects below {{threshold}} cm", {
            threshold: effectiveThresholdCm,
          })
        : t("Fines"),
    [t, effectiveThresholdCm],
  );

  // Calculate fines values (same logic as in characterisation module)
  const finesMass =
    (finesBelowThreshold?.massObject || 0) + (totalFinesMass || 0);
  const finesVolume =
    (finesBelowThreshold?.volumeObject || 0) + (totalFinesVolume || 0);
  const finesCount = finesBelowThreshold?.count || 0;

  const hasFines = finesMass > 0 || finesVolume > 0 || finesCount > 0;

  const outletChartData = useMemo(() => {
    const labels = [...(outletData?.labels ?? [])];
    const data = [...(outletData?.data ?? [])];
    const baseColors = ["#f58142", "#42f55d", "#bf42f5", "#2ab5b8"];
    if (hasFines && finesMass > 0) {
      const landfillLabel = t("Stockage");
      const landfillIndex = labels.findIndex(
        (label) => label === landfillLabel,
      );
      const finesTonnes = finesMass / 1000;
      if (landfillIndex >= 0) {
        data[landfillIndex] = (data[landfillIndex] || 0) + finesTonnes;
      } else {
        labels.push(landfillLabel);
        data.push(finesTonnes);
      }
    }
    if (data.length === 0) return null;
    const colors =
      data.length > baseColors.length ? [...baseColors, "#2ab5b8"] : baseColors;
    return { labels, data, colors };
  }, [outletData, hasFines, finesMass, t]);

  // Add fines to mass metrics
  const massLabels = [
    ...(metrics?.mass?.labels ?? []),
    ...(hasFines ? [finesLabel] : []),
  ];
  const massValues = [
    ...(metrics?.mass?.values ?? []),
    ...(hasFines && totalWasteMass > 0
      ? [(finesMass / totalWasteMass) * 100]
      : []),
  ];
  // Use default blue color for all materials, including fines
  const massColors = [
    ...(metrics?.mass?.colors ?? []),
    ...(hasFines ? ["rgba(75,192,192,0.6)"] : []), // Blue color for fines
  ];

  // Add fines to volume metrics
  const volumeLabels = [
    ...(metrics?.volume?.labels ?? []),
    ...(hasFines ? [finesLabel] : []),
  ];
  const volumeValues = [
    ...(metrics?.volume?.values ?? []),
    ...(hasFines && totalWasteVolume > 0
      ? [(finesVolume / totalWasteVolume) * 100]
      : []),
  ];

  // Add fines to objects data
  const dataNumberOfMaterial = [
    ...materialDelivery.map((material) => material.count),
    ...(hasFines ? [finesCount] : []),
  ];
  const labelMaterialSeparate = [
    ...materialDelivery.map((material) => t(material.cls)),
    ...(hasFines ? [finesLabel] : []),
  ];
  const colorMaterial = [
    ...materialDelivery.map((material) => formattedColorMaterial(material.cls)),
    ...(hasFines ? ["#FFA500"] : []), // Orange color for fines
  ];
  const {
    massChartRef,
    massTableRef,
    outletChartRef,
    outletTableRef,
    volumeChartRef,
    objectsChartRef,
    repChartRef,
    repTableRef,
    sizeChartRef,
    sizeMatrixRef,
  } = refs || {};

  const sizeDistributionFiltered = useMemo(() => {
    if (!sizeRangeDistribution) return [];
    return (sizeRangeDistribution.labels || [])
      .map((label, idx) => ({
        label,
        value: (sizeRangeDistribution.data || [])[idx],
      }))
      .filter((item) => item.label !== "total");
  }, [sizeRangeDistribution]);

  const sizeMatrixRows = useMemo(() => {
    if (!sizeRangeMatrix || sizeDistributionFiltered.length === 0) return null;
    const ranges = sizeDistributionFiltered.map((item) => item.label);
    const materials = Object.keys(sizeRangeMatrix || {}).filter((cls) => {
      const lower = (cls || "").toLowerCase();
      return (
        lower !== "unknown" && lower !== "inconnu" && cls !== "AUTRES (FINES)"
      );
    });
    const rows = materials
      .map((cls) => {
        const counts = sizeRangeMatrix[cls] || {};
        const total = ranges.reduce(
          (sum, range) => sum + (counts[range] || 0),
          0,
        );
        return { cls, counts, total };
      })
      .filter((r) => r.total > 0)
      .sort((a, b) => b.total - a.total);
    const maxValue = rows.reduce((max, row) => {
      const rowMax = ranges.reduce(
        (m, range) => Math.max(m, row.counts[range] || 0),
        0,
      );
      return Math.max(max, rowMax);
    }, 0);
    return { ranges, rows, maxValue };
  }, [sizeRangeMatrix, sizeDistributionFiltered]);
  /** Largeur mini pour les charts ; la matrice peut dépasser (max-content). */
  const PDF_CAPTURE_WIDTH = 1200;
  const containerStyle = renderOffscreen
    ? {
        top: "-9999px",
        left: "-9999px",
        zIndex: -50,
        position: "absolute",
        width: "max-content",
        minWidth: PDF_CAPTURE_WIDTH,
        overflow: "visible",
        boxSizing: "border-box",
      }
    : {
        position: "relative",
        width: "100%",
      };

  return (
    <div style={containerStyle}>
      {}
      {massChartRef && (
        <div
          ref={massChartRef}
          className="w-1/2 justify-center items-center bg-white rounded-xl"
          style={{ width: "700px", height: "550px" }}
        >
          <HorizontalBarChart
            dataChart={massValues}
            labelsChart={massLabels}
            unitValueChart="%"
            colorChart={massColors}
          />
        </div>
      )}
      {}
      {massTableRef &&
        parentCategoriesList.length > 0 &&
        characterizationType !== REGULATION_TYPES.RHW &&
        characterizationType !== "OMR" && (
          <div
            ref={massTableRef}
            className="w-4/6 char-reports-table-container"
          >
            <section className="w-full flex flex-col">
              <div className="char-reports-table-header">
                <div className="char-reports-table-header-label char-reports-table-header-col-3-5 text-lg">
                  {t("Type de déchets")}
                </div>
                <div className="char-reports-table-header-col-2-5">
                  <div className="char-reports-table-row-cell-half text-lg">
                    {t("Masse (kg)")}
                  </div>
                  <div className="char-reports-table-row-cell-half text-lg">
                    {t("Masse (%)")}
                  </div>
                </div>
              </div>
              <div className="pb-0">
                {parentCategoriesList.map(
                  (parentCategory, indexParentCategory) => (
                    <div
                      key={`parentcat-${indexParentCategory}`}
                      className="char-reports-table-row"
                    >
                      <div className="char-reports-table-row-cell char-reports-table-row-cell-3-5">
                        <div className="text-lg">
                          {capitalizeFirstLetter(
                            getParentCategoryLabelPlural(
                              t,
                              parentCategory.parentCategory,
                            ),
                          )}
                        </div>
                      </div>
                      <div className="char-reports-table-row-cell char-reports-table-row-cell-2-5">
                        <div className="char-reports-table-row-cell-half text-lg">
                          {totalWeight !== "" && totalWeight !== 0
                            ? toFrenchNumber(
                                (parentCategory.massObjects / totalWasteMass) *
                                  totalWeight,
                                1,
                              )
                            : "0,0"}{" "}
                          kg
                        </div>
                        <div className="char-reports-table-row-cell-half text-lg">
                          {totalWasteMass > 0
                            ? toFrenchNumber(
                                (parentCategory.massObjects / totalWasteMass) *
                                  100,
                                1,
                              )
                            : "0,0"}
                          %
                        </div>
                      </div>
                    </div>
                  ),
                )}
                {hasFines && finesMass > 0 && (
                  <div key="fines-row" className="char-reports-table-row">
                    <div className="char-reports-table-row-cell char-reports-table-row-cell-3-5">
                      <div className="text-lg">{finesLabel}</div>
                    </div>
                    <div className="char-reports-table-row-cell char-reports-table-row-cell-2-5">
                      <div className="char-reports-table-row-cell-half text-lg">
                        {totalWeight !== "" && totalWeight !== 0
                          ? toFrenchNumber(
                              (finesMass / totalWasteMass) * totalWeight,
                              1,
                            )
                          : "0,0"}{" "}
                        kg
                      </div>
                      <div className="char-reports-table-row-cell-half text-lg">
                        {totalWasteMass > 0
                          ? toFrenchNumber(
                              (finesMass / totalWasteMass) * 100,
                              1,
                            )
                          : "0,0"}
                        %
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
      {}
      {outletChartRef && outletChartData && (
        <div
          ref={outletChartRef}
          className="justify-center items-center bg-white rounded-xl"
          style={{
            width: "1200px",
            height: "1200px",
            padding: "40px",
            overflow: "visible",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MaterialCountingPieChart
            dataChart={outletChartData.data}
            labelChart={outletChartData.labels}
            materialTotal={totalWasteMass / 1000}
            centerDisplayValue={
              totalWeight != null &&
              totalWeight !== "" &&
              Number(totalWeight) !== 0
                ? Number(totalWeight) / 1000
                : undefined
            }
            colorChart={outletChartData.colors}
            showMaterialTotal={true}
            radius={["32%", "78%"]}
            title={t("Analyzed tonnes")}
            labelFontSize="25px"
          />
        </div>
      )}
      {}
      {outletTableRef && parentCategoriesList.length > 0 && (
        <div
          ref={outletTableRef}
          className="w-4/6 char-reports-table-container"
        >
          <section className="w-full flex flex-col">
            <div className="char-reports-table-header">
              <div className="char-reports-table-header-label char-reports-table-header-col-1-3 text-lg">
                {t("Type de déchets")}
              </div>
              <div className="char-reports-table-header-col-2-3">
                <div className="char-reports-table-row-cell-half text-lg">
                  {t("With recycling channel")}
                </div>
                <div className="char-reports-table-row-cell-half text-lg">
                  {t("Without recycling channel")}
                </div>
              </div>
            </div>
            <div className="pb-0">
              {parentCategoriesList.map(
                (parentCategory, indexParentCategory) => (
                  <div
                    key={`parentcat-${indexParentCategory}`}
                    className="char-reports-table-row"
                  >
                    <div className="char-reports-table-row-cell char-reports-table-row-cell-1-3">
                      <div className="text-lg">
                        {capitalizeFirstLetter(
                          getParentCategoryLabelPlural(
                            t,
                            parentCategory.parentCategory,
                          ),
                        )}
                      </div>
                    </div>
                    <div className="char-reports-table-row-cell char-reports-table-row-cell-2-3">
                      <div className="char-reports-table-row-cell-col">
                        <span className="char-reports-table-row-cell-col-main text-lg">
                          {totalWeight !== "" && totalWeight !== 0
                            ? toFrenchNumber(
                                (parentCategory.massInOutlet / totalWasteMass) *
                                  totalWeight,
                                1,
                              )
                            : "0,0"}{" "}
                          kg
                        </span>
                        <span className="char-reports-table-row-cell-col-sub text-base">
                          {parentCategory.massInOutlet > 0
                            ? toFrenchNumber(
                                (parentCategory.massInOutlet / totalWasteMass) *
                                  100,
                                1,
                              )
                            : "0,0"}
                          %
                        </span>
                      </div>
                      <div className="char-reports-table-row-cell-col">
                        <span className="char-reports-table-row-cell-col-main text-lg">
                          {totalWeight !== "" && totalWeight !== 0
                            ? toFrenchNumber(
                                (parentCategory.massNotInOutlet /
                                  totalWasteMass) *
                                  totalWeight,
                                1,
                              )
                            : "0,0"}{" "}
                          kg
                        </span>
                        <span className="char-reports-table-row-cell-col-sub text-base">
                          {parentCategory.massNotInOutlet > 0
                            ? toFrenchNumber(
                                (parentCategory.massNotInOutlet /
                                  totalWasteMass) *
                                  100,
                                1,
                              )
                            : "0,0"}
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                ),
              )}
              {hasFines && finesMass > 0 && (
                <div
                  key="fines-row-outlet-pdf"
                  className="char-reports-table-row"
                >
                  <div className="char-reports-table-row-cell char-reports-table-row-cell-1-3">
                    <div className="text-lg">{t("Autres (FINES)")}</div>
                  </div>
                  <div className="char-reports-table-row-cell char-reports-table-row-cell-2-3">
                    <div className="char-reports-table-row-cell-col">
                      <span className="char-reports-table-row-cell-col-main text-lg">
                        0,0 kg
                      </span>
                      <span className="char-reports-table-row-cell-col-sub text-base">
                        0,0%
                      </span>
                    </div>
                    <div className="char-reports-table-row-cell-col">
                      <span className="char-reports-table-row-cell-col-main text-lg">
                        {totalWeight !== "" && totalWeight !== 0
                          ? toFrenchNumber(
                              (finesMass / totalWasteMass) * totalWeight,
                              1,
                            )
                          : "0,0"}{" "}
                        kg
                      </span>
                      <span className="char-reports-table-row-cell-col-sub text-base">
                        {totalWasteMass > 0
                          ? toFrenchNumber(
                              (finesMass / totalWasteMass) * 100,
                              1,
                            )
                          : "0,0"}
                        %
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
      {}
      {volumeChartRef && (
        <div
          ref={volumeChartRef}
          className="w-1/2 justify-center items-center bg-white rounded-xl"
          style={{ width: "700px", height: "550px" }}
        >
          <HorizontalBarChart
            dataChart={volumeValues}
            labelsChart={volumeLabels}
            unitValueChart="%"
          />
        </div>
      )}
      {}
      {objectsChartRef &&
        materialTotal !== undefined &&
        materialTotal !== 0 && (
          <div
            ref={objectsChartRef}
            className="justify-center items-center bg-white rounded-xl"
            style={{
              width: "1200px",
              height: "1200px",
              padding: "40px",
              overflow: "visible",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MaterialCountingPieChart
              dataChart={dataNumberOfMaterial}
              labelChart={labelMaterialSeparate}
              materialTotal={materialTotal}
              colorChart={colorMaterial}
              showMaterialTotal={true}
              radius={["30%", "70%"]}
              labelFontSize="25px"
            />
          </div>
        )}
      {}
      {characterizationType === "EPR" &&
        repChartRef &&
        repData?.data &&
        Object.keys(repData.data).length > 0 && (
          <div
            ref={repChartRef}
            className="justify-center items-center bg-white rounded-xl"
            style={{
              width: "1200px",
              height: "1200px",
              padding: "40px",
              overflow: "visible",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {repData?.repChartData && repData.repChartData.length > 0 && (
              <MaterialCountingPieChart
                dataChart={(() => {
                  const volumeTotal = Number(repData.volumeTotal) || 0;
                  if (!volumeTotal) {
                    return repData.repChartData.map(() => 0);
                  }
                  return repData.repChartData.map((item) => {
                    const percent = parseFloat(item?.value) || 0;
                    return (percent * volumeTotal) / 100;
                  });
                })()}
                labelChart={repData.repChartData.map((item) => item.name)}
                materialTotal={Number(repData.volumeTotal) || 0}
                colorChart={["#f58142", "#42f55d", "#bf42f5", "#2ab5b8"]}
                showMaterialTotal={false}
                radius={["30%", "70%"]}
                labelFontSize="25px"
              />
            )}
          </div>
        )}
      {}
      {characterizationType === "EPR" &&
        repTableRef &&
        repData?.data &&
        Object.keys(repData.data).length > 0 && (
          <div ref={repTableRef} className="w-4/6 char-reports-table-container">
            <section className="w-full flex flex-col">
              <div className="w-full flex flex-row justify-between border-b pb-3 mb-2 px-4 pt-4">
                <div className="font-semibold text-gray-600 w-3/5">
                  {t("Associated EPR")}
                </div>
                <div className="flex w-2/5 justify-end text-gray-600 font-semibold">
                  <div className="w-1/2 text-center">{t("Mass")} (%)</div>
                  <div className="w-1/2 text-center">{t("Mass")} (kg)</div>
                </div>
              </div>
              <div className="pb-2">
                {sortRepAssociatedKeys(Object.keys(repData.data), t).map(
                  (key, index) => (
                    <div
                      key={`rep-${index}`}
                      className="flex justify-between items-center py-3 border-b last:border-none"
                    >
                      <div className="px-4 w-3/5 text-gray-700">
                        <div className="text-base font-medium">{t(key)}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {repData.data[key]?.objects &&
                          repData.data[key]?.objects.length
                            ? repData.data[key].objects
                                .map((material) => t(material))
                                .sort()
                                .join(", ")
                            : repData.data[key]?.description}
                        </div>
                      </div>
                      <div className="px-4 flex w-2/5 justify-end text-gray-700 font-medium">
                        <div className="w-1/2 text-center">
                          {repData.data[key]?.massObject && totalWasteMass > 0
                            ? toFrenchNumber(
                                (repData.data[key].massObject /
                                  totalWasteMass) *
                                  100,
                                1,
                              )
                            : "0,0"}
                          %
                        </div>
                        <div className="w-1/2 text-center">
                          {totalWeight !== "" &&
                          repData.data[key]?.massObject &&
                          totalWasteMass > 0
                            ? toFrenchNumber(
                                (repData.data[key].massObject /
                                  totalWasteMass) *
                                  totalWeight,
                                1,
                              )
                            : "-"}{" "}
                          {totalWeight !== "" && "kg"}
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </section>
          </div>
        )}
      {characterizationType === REGULATION_TYPES.CLASSIC &&
        !isBenneCharacterization &&
        sizeChartRef &&
        sizeDistributionFiltered.length > 0 && (
          <div
            ref={sizeChartRef}
            className="bg-white"
            style={{
              // Même largeur que la matrice pour un rendu pleine page cohérent.
              width: "1600px",
              height: "480px",
              padding: "4px",
              overflow: "visible",
              boxSizing: "border-box",
            }}
          >
            <div style={{ width: "100%", height: "100%" }}>
              <VerticalBarChart
                dataChart={sizeDistributionFiltered.map((x) => x.value)}
                labelsChart={sizeDistributionFiltered.map((x) => `${x.label}m`)}
                unitValueChart=""
                colorChart="#4BC0C0"
                thresholdCm={
                  effectiveThresholdCm > 0 ? effectiveThresholdCm : null
                }
              />
            </div>
          </div>
        )}
      {characterizationType === REGULATION_TYPES.CLASSIC &&
        !isBenneCharacterization &&
        sizeMatrixRef &&
        sizeMatrixRows &&
        sizeMatrixRows.rows.length > 0 && (
          <div
            ref={sizeMatrixRef}
            data-pdf-size-matrix=""
            className="bg-white"
            style={{
              width: "1600px",
              padding: "4px",
              overflow: "visible",
              boxSizing: "border-box",
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <h5 className="char-text-16 char-font-semibold char-text-primary">
                {t("Size distribution matrix")}
              </h5>
              <div className="char-text-14 char-text-secondary">
                {t("Darker = more objects")}
              </div>
            </div>
            <div
              className="char-reports-matrix-container"
              style={{
                overflow: "visible",
                maxWidth: "none",
                width: "100%",
              }}
            >
              <table
                className="char-reports-matrix-table"
                style={{
                  width: "100%",
                  minWidth: "100%",
                  tableLayout: "fixed",
                  borderCollapse: "collapse",
                }}
              >
                {/* Colonne matière large : sinon table-layout:fixed écrase les labels sur les cellules. */}
                <colgroup>
                  <col style={{ width: "220px" }} />
                  {sizeMatrixRows.ranges.map((range) => (
                    <col key={range} />
                  ))}
                </colgroup>
                <thead className="char-reports-matrix-table-header">
                  <tr>
                    <th
                      className="char-reports-matrix-table-header-cell"
                      style={{
                        width: "220px",
                        maxWidth: "220px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        backgroundColor: "#fff",
                        paddingRight: "8px",
                      }}
                    >
                      {t("Material")}
                    </th>
                    {sizeMatrixRows.ranges.map((range) => (
                      <th
                        key={range}
                        className="char-reports-matrix-table-header-cell char-reports-matrix-table-header-cell-center"
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "11px",
                        }}
                      >
                        {range}m
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sizeMatrixRows.rows.map((row) => (
                    <tr
                      key={row.cls}
                      className="char-reports-matrix-table-body-row"
                    >
                      <td
                        className="char-reports-matrix-table-body-row-cell char-reports-matrix-table-body-row-cell-material"
                        style={{
                          width: "220px",
                          maxWidth: "220px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          backgroundColor: "#fff",
                          paddingRight: "8px",
                        }}
                        title={
                          row.cls === "AUTRES (FINES)"
                            ? t("Autres (FINES)")
                            : t(row.cls)
                        }
                      >
                        {row.cls === "AUTRES (FINES)"
                          ? t("Autres (FINES)")
                          : t(row.cls)}
                      </td>
                      {sizeMatrixRows.ranges.map((range) => {
                        const value = row.counts[range] || 0;
                        const intensity =
                          sizeMatrixRows.maxValue > 0
                            ? value / sizeMatrixRows.maxValue
                            : 0;
                        const background =
                          value === 0
                            ? "transparent"
                            : `rgba(75, 192, 192, ${0.15 + 0.65 * intensity})`;
                        const isBelowThreshold =
                          effectiveThresholdCm > 0 &&
                          Number(range.split("-")[1]) * 100 <=
                            effectiveThresholdCm;
                        const mutedOverlay = isBelowThreshold
                          ? "rgba(156, 163, 175, 0.18)"
                          : null;

                        return (
                          <td
                            key={range}
                            className="char-reports-matrix-table-body-row-cell"
                            style={{
                              background:
                                mutedOverlay && value > 0
                                  ? `linear-gradient(${mutedOverlay}, ${mutedOverlay}), ${background}`
                                  : mutedOverlay
                                    ? mutedOverlay
                                    : background,
                            }}
                          >
                            {value > 0 ? value : ""}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  );
};
